'use client'

import React from 'react'

export type LinkComponentType = React.ElementType<
	React.AnchorHTMLAttributes<HTMLAnchorElement>
>

/**
 * Universal link component context. Use LinkProvider in a Client Component (e.g. layout)
 * so all components (MButton, MProgramCard, MAnnualProgramPreview, etc.) can use next/link
 * without receiving it as a prop. Avoids "Functions cannot be passed to Client Components"
 * when using Next.js App Router with Server Components.
 */
export const LinkContext = React.createContext<LinkComponentType | undefined>(
	undefined
)

/**
 * Provider for Next.js App Router: wrap your app (or layout) in a Client Component
 * and pass next/link here so all link-using components get it from context.
 *
 * @example
 * ```tsx
 * 'use client'
 * import Link from 'next/link'
 * import { LinkProvider } from '@medante/react-components'
 *
 * export default function Layout({ children }) {
 *   return (
 *     <LinkProvider value={Link}>
 *       {children}
 *     </LinkProvider>
 *   )
 * }
 * ```
 */
export const LinkProvider: React.FC<{
	value: LinkComponentType
	children: React.ReactNode
}> = ({ value, children }) => (
	<LinkContext.Provider value={value}>{children}</LinkContext.Provider>
)
